import {
  GatewayAssujetti,
  GatewayEntreprise,
  GatewayFormationContinue,
} from "@conformite/gateway";
import {
  IconStatus,
  Link,
  isMulti,
  Form,
  Button,
  Card,
  FormTitle,
  displaySucessToast,
} from "@endya-conformite/ui-kit";
import ChevronLeft from "@endya-conformite/ui-kit/src/assets/icons/chevron-left.svg?react";
import { GatewayFormationContinueApi } from "@src/api/conformite/formationContinue.api";
import {
  ConformiteFormFormationIobspContent,
  useFormationContinueIobsp,
} from "@src/assujettis/AssujettiPaneContent/pages/AssujettiParcoursConformite/Steps/FormsFormation/ConformiteFormFormationIOBSP/ConformiteFormFormationIOBSPContent";
import { MainContentPageTemplate } from "@src/components/MainPageTemplate/MainContentPageTemplate";
import { NoEntityForMillesime } from "@src/components/NoEntityForMillesime/NoEntityForMillesime";
import { useOnce } from "@src/hooks/useOnce";
import { useAssujetti } from "@src/store/store.assujetti";
import { useEntreprises } from "@src/store/store.entreprise";
import { useMillesime } from "@src/store/store.millesime";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import styles from "../assujettis/AssujettiForm.module.scss";

type ConformiteFormationContinueContentProps = {
  assujetti: GatewayAssujetti.AssujettiWithAvancement;
  entreprise: GatewayEntreprise.EntrepriseAControllerResponseType;
  fromAxe: string | null;
  formationContinue: GatewayFormationContinue.FormationContinueIobsp | null;
};
function ConformiteFormationContinueContent({
  assujetti,
  fromAxe,
  entreprise,
  formationContinue,
}: ConformiteFormationContinueContentProps) {
  const isMultiActivite = isMulti(entreprise.categoriesActivites);
  const {
    lastVerifiedAt,
    onSubmit,
    formConfig,
    fields,
    remove,
    append,
    totalHours,
    soumisFormationContinue,
  } = useFormationContinueIobsp(
    formationContinue,
    { assujetti },
    () => {
      displaySucessToast("La conformité a bien été mise à jour");
    },
    [
      entreprise.sousCategoriesIOBSP ?? [],
      entreprise.sousCategorieCOBSP ?? [],
    ].flat(),
    !isMultiActivite
  );
  useOnce(() => {
    if (formationContinue !== null) formConfig.trigger().catch(console.error);
  });
  if (!entreprise || !assujetti) return null;

  return (
    <MainContentPageTemplate
      title={
        <FormTitle className={styles.header}>
          <h1 className={styles.title}>
            <b>Formation Continue IOBSP</b> - {assujetti.surname}{" "}
            {assujetti.name}
          </h1>
          <IconStatus
            isValid={assujetti.avancement.formationContinueIobsp === 100}
          />
        </FormTitle>
      }
      actionNavigation={
        <Link
          to={`/conformite/formation-continue/iobsp/${entreprise.siren}${
            fromAxe === null ? "" : "?fromAxe"
          }`}
        >
          <Button variant="text" icon={<ChevronLeft />}>
            Retour
          </Button>
        </Link>
      }
    >
      <Card>
        <Form formConfig={formConfig} onSubmit={onSubmit}>
          <ConformiteFormFormationIobspContent
            hideTitle
            data={{ assujetti }}
            lastVerifiedAt={lastVerifiedAt}
            fields={fields}
            remove={remove}
            append={append}
            sousActivitesFinancieres={[
              ...(entreprise?.sousCategorieCOBSP ?? []),
              ...(entreprise?.sousCategoriesIOBSP ?? []),
            ]}
            isMultiActivite={isMultiActivite}
            totalHours={totalHours}
            soumisFormationContinue={soumisFormationContinue}
          />
        </Form>
        <div className={styles.action}>
          <Button
            onClick={() => {
              onSubmit(formConfig.getValues()).catch((e) => {
                console.error(e);
              });
            }}
          >
            Sauvegarder
          </Button>
        </div>
      </Card>
    </MainContentPageTemplate>
  );
}

export function ConformiteFormationContinueIobsp() {
  const { entreprisesBySiren } = useEntreprises();
  const { assujettis } = useAssujetti();
  const { selected: millesime } = useMillesime();
  const { siren, id } = useParams();
  const entreprise = entreprisesBySiren?.[siren || ""];
  const assujetti = assujettis?.[id || ""];
  const [query] = useSearchParams();
  const fromAxe = query.get("fromAxe");
  const [formationContinue, setFormationContinue] =
    useState<GatewayFormationContinue.FormationContinueIobsp | null>();
  useEffect(() => {
    if (!assujetti) return;
    GatewayFormationContinueApi.getIOBSP(assujetti.id, assujetti.millesime)
      .then((data) => {
        setFormationContinue(data);
      })
      .catch((e) => {
        console.error(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assujetti]);
  const isDataLoaded =
    entreprisesBySiren !== undefined && assujettis !== undefined;
  if (isDataLoaded && (!entreprise || !assujetti)) {
    return (
      <MainContentPageTemplate title="Formation Continue IOBSP">
        <NoEntityForMillesime millesime={millesime} entityName="société" />
      </MainContentPageTemplate>
    );
  }
  if (!entreprise || !assujetti || !siren || formationContinue === undefined)
    return null;
  return (
    <ConformiteFormationContinueContent
      entreprise={entreprise}
      assujetti={assujetti}
      fromAxe={fromAxe}
      formationContinue={formationContinue}
    />
  );
}
